import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Upload, Trash2, Tag } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SECTIONS = [
  "hero", "experience", "story", "property", "trails",
  "road-in", "services", "attractions", "gallery", "guestbook",
];

export default function PhotoManager({ authHeader }) {
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [section, setSection] = useState("gallery");
  const [label, setLabel] = useState("");
  const [editingId, setEditingId] = useState(null);

  const fetchPhotos = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/photos`, authHeader);
      setPhotos(res.data.photos);
    } catch (e) {
      console.error(e);
    }
  }, [authHeader]);

  useEffect(() => { fetchPhotos(); }, [fetchPhotos]);

  const handleUpload = async (e) => {
    const files = e.target.files;
    if (!files?.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("section", section);
        formData.append("label", label || file.name);
        await axios.post(`${API}/admin/photos/upload`, formData, {
          ...authHeader,
          headers: { ...authHeader.headers, "Content-Type": "multipart/form-data" },
        });
      }
      setLabel("");
      fetchPhotos();
    } catch (err) {
      console.error("Upload error:", err);
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this photo?")) return;
    await axios.delete(`${API}/admin/photos/${id}`, authHeader);
    fetchPhotos();
  };

  const handleUpdate = async (id, updates) => {
    await axios.put(`${API}/admin/photos/${id}`, updates, authHeader);
    setEditingId(null);
    fetchPhotos();
  };

  return (
    <div data-testid="photo-manager">
      {/* Upload area */}
      <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
        <h3 className="font-display text-wc-forest text-lg font-semibold mb-4 flex items-center gap-2">
          <Upload className="w-5 h-5" /> Upload Photos
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
          <div>
            <Label className="font-body text-sm text-gray-600">Section</Label>
            <select
              value={section}
              onChange={(e) => setSection(e.target.value)}
              className="mt-1 w-full rounded-md border border-gray-200 px-3 py-2 font-body text-sm"
              data-testid="photo-section-select"
            >
              {SECTIONS.map((s) => (
                <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1).replace("-", " ")}</option>
              ))}
            </select>
          </div>
          <div>
            <Label className="font-body text-sm text-gray-600">Label / Description</Label>
            <Input
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="e.g. Sunset over the Bay"
              className="mt-1"
              data-testid="photo-label-input"
            />
          </div>
          <div className="flex items-end">
            <label className="w-full">
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={handleUpload}
                className="hidden"
                data-testid="photo-file-input"
              />
              <Button
                type="button"
                disabled={uploading}
                className="w-full bg-wc-teal text-white hover:bg-wc-forest font-body"
                onClick={() => document.querySelector('[data-testid="photo-file-input"]').click()}
                data-testid="photo-upload-button"
              >
                {uploading ? "Uploading..." : "Choose & Upload"}
              </Button>
            </label>
          </div>
        </div>
      </div>

      {/* Photo grid */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="font-display text-wc-forest text-lg font-semibold mb-4">
          All Photos ({photos.length})
        </h3>
        {photos.length === 0 ? (
          <p className="font-body text-gray-400 text-sm">No photos uploaded yet.</p>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {photos.map((photo) => (
              <div key={photo.id} className="group relative border border-gray-100 rounded-md overflow-hidden">
                <img
                  src={`${API}/photos/${photo.id}/image`}
                  alt={photo.label || photo.original_filename}
                  className="w-full aspect-square object-cover"
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => setEditingId(editingId === photo.id ? null : photo.id)}
                    className="text-xs"
                  >
                    <Tag className="w-3 h-3 mr-1" /> Edit
                  </Button>
                  <Button
                    size="sm"
                    variant="destructive"
                    onClick={() => handleDelete(photo.id)}
                    className="text-xs"
                  >
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
                <div className="p-2">
                  <span className="inline-block px-2 py-0.5 bg-wc-teal/10 text-wc-teal text-xs rounded font-body">
                    {photo.section}
                  </span>
                  <p className="font-body text-gray-500 text-xs mt-1 truncate">{photo.label || photo.original_filename}</p>
                </div>
                {editingId === photo.id && (
                  <div className="p-2 border-t space-y-2">
                    <select
                      defaultValue={photo.section}
                      onChange={(e) => handleUpdate(photo.id, { section: e.target.value })}
                      className="w-full text-xs rounded border px-2 py-1"
                    >
                      {SECTIONS.map((s) => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                    <Input
                      defaultValue={photo.label}
                      placeholder="Label"
                      onBlur={(e) => handleUpdate(photo.id, { label: e.target.value })}
                      className="text-xs h-7"
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
